import {
  LogOut,
  Menu,
  Search,
  User as UserIcon,
  Upload,
  ChevronDown,
  PlaneTakeoff,
  Check,
  Building2,
} from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { useIsClient } from '@/hooks/useIsClient';
import { useOrganization } from '@/api/organizations';
import { useUIStore } from '@/stores/ui';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { NotificationsBell } from '@/components/notifications/NotificationsBell';
import { Breadcrumbs, type Crumb } from './Breadcrumbs';
import { QuickUploadModal } from './QuickUploadModal';
import { BillerBayAppSwitcher } from './BillerBayAppSwitcher';
import { useLogout } from '@/api/auth';

const ROLE_LABEL: Record<string, string> = {
  sysadmin: 'System admin',
  manager: 'Manager',
  staff: 'Staff',
  client_admin: 'Client admin',
  client_user: 'Client user',
};

function initialsOf(fullName: string | undefined, email: string | undefined): string {
  const source = (fullName ?? '').trim();
  if (source) {
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return 'U';
}

export function TopBar({ crumbs }: { crumbs?: Crumb[] }) {
  const { user, logout, activeOrgId, organizations, setActiveOrg } = useAuth();
  const isClient = useIsClient();
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const logoutMut = useLogout();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  const openPalette = () => useUIStore.getState().setCommandPaletteOpen(true);

  const handleLogout = async () => {
    try {
      await logoutMut.mutateAsync();
    } catch {
      /* ignore */
    }
    logout();
    navigate('/login');
  };

  const initials = initialsOf(user?.full_name, user?.email);
  const isClientRole = user?.role === 'client_admin' || user?.role === 'client_user';
  // For multi-org client users, resolve org name from activeOrgId; fallback to user.organization_id
  const resolvedOrgId = isClientRole
    ? (activeOrgId ?? user?.organization_id ?? undefined)
    : undefined;
  const org = useOrganization(resolvedOrgId);
  const orgName = org.data?.name;
  const roleLabel = (() => {
    if (!user?.role) return '';
    if (user.role === 'client_admin') return orgName ? `${orgName} Admin` : 'Admin';
    if (user.role === 'client_user') return orgName ? `${orgName} User` : 'User';
    return ROLE_LABEL[user.role] ?? user.role;
  })();
  const isStaffRole = user?.role && ['sysadmin', 'manager', 'staff'].includes(user.role);
  const isOoo = isStaffRole && user?.is_ooo === true;

  // Show org switcher for client users who belong to multiple orgs
  const showOrgSwitcher = isClientRole && organizations.length > 1;

  return (
    <>
      <header className="flex items-center gap-2 px-3 md:px-4 h-14 bg-[#12122C] border-b border-white/10 shrink-0">

        {/* Hamburger — mobile only */}
        <button
          type="button"
          onClick={toggleSidebar}
          className="md:hidden p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors shrink-0"
          aria-label="Open navigation menu"
        >
          <Menu size={20} />
        </button>

        {/* Breadcrumbs / context */}
        <div className="flex-1 min-w-0 ml-1">
          {crumbs && crumbs.length > 0 ? (
            <Breadcrumbs items={crumbs} />
          ) : (
            <span className="text-sm text-white/60 truncate block">
              {isClient
                ? `Welcome back${user ? `, ${user.full_name}` : ''}`
                : `Welcome${user ? `, ${user.full_name}` : ''}`}
            </span>
          )}
        </div>

        {/* Org switcher — client users with multiple orgs */}
        {showOrgSwitcher && (
          <Dropdown
            align="right"
            trigger={
              <button
                type="button"
                className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white/10 hover:bg-white/15 border border-white/20 transition-colors text-sm text-white/80 max-w-[180px]"
                aria-label="Switch organization"
              >
                <Building2 size={13} className="shrink-0 text-white/60" />
                <span className="truncate">{orgName ?? 'Select org'}</span>
                <ChevronDown size={12} className="shrink-0 text-white/50" aria-hidden />
              </button>
            }
          >
            <div className="px-3 py-2 border-b border-[#E3E3F1] min-w-[200px]">
              <div className="text-xs font-semibold uppercase tracking-wide text-[#676687]">Switch organization</div>
            </div>
            {organizations.map((o) => (
              <DropdownItem
                key={o.id}
                onClick={() => {
                  setActiveOrg(o.id);
                  queryClient.invalidateQueries();
                  navigate('/');
                }}
              >
                <span className="inline-flex items-center gap-2 w-full">
                  <Building2 size={13} className="shrink-0 text-[#0410BD]" />
                  <span className="flex-1 truncate">{o.name}</span>
                  {activeOrgId === o.id && <Check size={13} className="shrink-0 text-[#0410BD]" />}
                </span>
              </DropdownItem>
            ))}
          </Dropdown>
        )}

        {/* Upload CTA */}
        <button
          onClick={() => setUploadOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[6px] text-sm font-semibold text-white bg-[#0410BD] hover:bg-[#3F4CFF] transition-colors shrink-0"
          aria-label="Upload files"
          title="Upload files"
          data-tour="upload"
        >
          <Upload size={15} />
          <span className="hidden sm:inline">Upload</span>
        </button>

        {/* Search pill — opens the command palette */}
        <button
          type="button"
          onClick={openPalette}
          className="hidden sm:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 hover:bg-white/15 border border-white/15 hover:border-white/30 transition-colors min-w-[180px] lg:min-w-[240px]"
          title="Search (Ctrl+K)"
          aria-label="Search"
          data-tour="cmdk"
        >
          <Search size={14} className="text-white/50 shrink-0" />
          <span className="flex-1 text-left text-sm text-white/40">Search…</span>
          <kbd className="text-[10px] font-mono text-white/35 border border-white/20 rounded px-1 py-0.5 leading-none shrink-0">⌘K</kbd>
        </button>

        {/* Mobile search icon */}
        <button
          type="button"
          onClick={openPalette}
          className="sm:hidden p-2 rounded-md text-white/70 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Search"
        >
          <Search size={18} />
        </button>

        <NotificationsBell />

        {/* BillerBay app switcher — staff/admin only */}
        {isStaffRole && <BillerBayAppSwitcher />}

        {/* Profile menu */}
        <Dropdown
          align="right"
          trigger={
            <button
              type="button"
              className="group inline-flex items-center gap-2 pl-1.5 pr-2 py-1 rounded-md border border-transparent hover:border-white/20 hover:bg-white/10 transition-colors"
              aria-label="Open profile menu"
              title={user ? `${user.full_name} (${user.email})` : 'Profile menu'}
              data-tour="profile-menu"
            >
              <span className="relative shrink-0">
                <span className="w-8 h-8 rounded-full bg-[#EFF0FF] text-[#0410BD] flex items-center justify-center text-xs font-bold tracking-wide">
                  {initials}
                </span>
                {isOoo && (
                  <span
                    className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#FDE68A] border-2 border-[#12122C] flex items-center justify-center"
                    title="Out of office"
                  >
                    <PlaneTakeoff size={8} className="text-[#B45309]" />
                  </span>
                )}
              </span>

              <span className="hidden sm:flex flex-col text-left leading-tight min-w-0 max-w-[160px]">
                <span className="text-sm font-medium text-white truncate">
                  {user?.full_name || user?.email || 'You'}
                </span>
                <span className="text-[11px] text-white/50 truncate">{roleLabel}</span>
              </span>

              <ChevronDown
                size={14}
                className="hidden sm:inline shrink-0 text-white/40 group-hover:text-white/70 transition-colors"
                aria-hidden
              />
            </button>
          }
        >
          <div className="px-3 py-2.5 border-b border-[#E3E3F1] min-w-[220px]">
            <div className="text-sm font-semibold text-[#12122C] truncate">
              {user?.full_name || 'Signed in'}
            </div>
            <div className="text-xs text-[#676687] truncate">{user?.email}</div>
            <div className="mt-1 flex items-center gap-2">
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#EFF0FF] text-[#0410BD]">
                {roleLabel}
              </span>
              {isOoo && (
                <span
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide bg-[#FFFBEB] text-[#B45309]"
                  title={user?.ooo_note ?? undefined}
                >
                  <PlaneTakeoff size={10} /> OOO
                </span>
              )}
            </div>
          </div>

          <DropdownItem onClick={() => navigate('/profile')}>
            <span className="inline-flex items-center gap-2">
              <UserIcon size={14} />
              Profile &amp; settings
            </span>
          </DropdownItem>
          <DropdownItem onClick={handleLogout} danger>
            <span className="inline-flex items-center gap-2">
              <LogOut size={14} />
              Sign out
            </span>
          </DropdownItem>
        </Dropdown>
      </header>

      <QuickUploadModal open={uploadOpen} onClose={() => setUploadOpen(false)} />
    </>
  );
}
